import { skipToken } from '@apollo/client/react'
import { match } from 'ts-pattern'
import { useCheckTokenForArgs } from 'src/utils/token'
import { baseApolloClient, cache } from './client'
import type { GenerationModelDetailFragment } from './generated/graphql'
import {
  ArchiveTrainingTaskDocument,
  DeleteGenerationModelDocument,
  GenerationModelBaseFragmentDoc,
  GenerationModelDetailFragmentDoc,
  GenerationModelMarkType,
  GenerationModelVersionBaseFragmentDoc,
  GenerationModelVersionDetailFragmentDoc,
  GetFileUploadRecordDocument,
  GetGenerationModelByVersionIdDocument,
  GetGenerationModelByVersionIdForArtworkDocument,
  GetGenerationModelDetailByVersionIdDocument,
  GetGenerationModelDocument,
  GetGenerationModelVersionDocument,
  GetPrivateGenerationModelTotalCountDocument,
  GetTrainingTaskByIdDocument,
  ListGenerationModelsDocument,
  ListGenerationModelsWithTrainingTaskDocument,
  ListGenerationModelVersionsDocument,
  ListMyBookmarkedGenerationModelsDocument,
  ListUserLikedGenerationModelDocument,
  MarkGenerationModelDocument,
  ReviewMemberOnlyModelEarlyAccessDocument,
  UpsertGenerationModelDocument,
  UpsertGenerationModelVersionDocument,
} from './generated/graphql'
import {
  createFetchQueryHelper,
  createMutationHelper,
  createQueryHook,
  infiniteQueryHook,
  modifyQueryHook,
  pagedQueryHook,
} from './utils'

// ==================================================
// Model
// ==================================================

export const useGetGenerationModel = modifyQueryHook(
  createQueryHook(GetGenerationModelDocument),
  {
    transform: (id: string) => ({ id }),
    reduce: r => r?.generationModel,
  },
)
export const updateGetGenerationModel = (
  id: string,
  newData: GenerationModelDetailFragment,
) => {
  cache.updateQuery(
    {
      query: GetGenerationModelDocument,
      variables: { id: id },
    },
    () => {
      return {
        generationModel: { __typename: 'GenerationModel' as const, ...newData },
      }
    },
  )
}

export const fetchGetGenerationModel = createFetchQueryHelper(
  GetGenerationModelDocument,
)

export const refetchGetGenerationModel = (id: string) => {
  baseApolloClient.refetchQueries({
    updateCache(cache) {
      cache.evict({
        id: cache.identify({ __typename: 'GenerationModel' as const, id }),
      })
    },
  })
}

const useListGenerationModels = modifyQueryHook(
  createQueryHook(ListGenerationModelsDocument),
  {
    reduce: r => r?.generationModels,
  },
)

export const useInfiniteListGenerationModels = infiniteQueryHook(
  useListGenerationModels,
)
export const usePagedListGenerationModels = pagedQueryHook(
  useListGenerationModels,
)

export const useGetGenerationModelTotalCount = modifyQueryHook(
  createQueryHook(GetPrivateGenerationModelTotalCountDocument),
  { reduce: r => r?.generationModels.totalCount },
)

export const [useUpsertGenerationModel, executeUpsertGenerationModel] =
  createMutationHelper(UpsertGenerationModelDocument, {
    update: (cache, result, args) => {
      const newModel = result.data?.upsertGenerationModel
      if (!newModel) return
      cache.writeFragment({
        fragment: GenerationModelBaseFragmentDoc,
        fragmentName: 'GenerationModelBase',
        id: cache.identify(newModel),
        data: newModel,
      })
      cache.writeFragment({
        fragment: GenerationModelDetailFragmentDoc,
        fragmentName: 'GenerationModelDetail',
        id: cache.identify(newModel),
        data: newModel,
      })
    },
  })

export const fetchGetFileUploadRecord = createFetchQueryHelper(
  GetFileUploadRecordDocument,
)

// ==================================================
// Model Version
// ==================================================

export const useGetGenerationModelByVersionId = modifyQueryHook(
  createQueryHook(GetGenerationModelByVersionIdDocument),
  {
    transform: (id: string) => ({ id }),
    reduce: r => r?.generationModelVersion || undefined,
  },
)
export const fetchGetGenerationModelByVersionId = createFetchQueryHelper(
  GetGenerationModelByVersionIdDocument,
)

export const fetchGetGenerationModelVersion = createFetchQueryHelper(
  GetGenerationModelVersionDocument,
)

export const useListGenerationModelVersions = modifyQueryHook(
  createQueryHook(ListGenerationModelVersionsDocument),
  {
    reduce: r => r?.generationModelVersions,
  },
)

export const fetchListGenerationModelVersions = createFetchQueryHelper(
  ListGenerationModelVersionsDocument,
)

export const [useDeleteGenerationModel, executeDeleteGenerationModel] =
  createMutationHelper(DeleteGenerationModelDocument)

export const [
  useUpsertGenerationModelVersion,
  executeUpsertGenerationModelVersion,
] = createMutationHelper(UpsertGenerationModelVersionDocument, {
  update: (cache, result, args) => {
    const newModelVersion = result.data?.upsertGenerationModelVersion
    if (!newModelVersion) return
    cache.writeFragment({
      fragment: GenerationModelVersionBaseFragmentDoc,
      fragmentName: 'GenerationModelVersionBase',
      id: cache.identify(newModelVersion),
      data: newModelVersion,
    })
    cache.writeFragment({
      fragment: GenerationModelVersionDetailFragmentDoc,
      fragmentName: 'GenerationModelVersionDetail',
      id: cache.identify(newModelVersion),
      data: newModelVersion,
    })
  },
})

/**
 * Same as useGenerationModelByVersionId, but adding likedCount for artwork detail page
 */
export const useGetGenerationModelByVersionIdForArtwork = modifyQueryHook(
  createQueryHook(GetGenerationModelByVersionIdForArtworkDocument),
  {
    transform: (id: string) => ({ id }),
    reduce: r => r?.generationModelVersion || undefined,
  },
)

/**
 * Impacts performance
 * Should only be used in ModelDetail
 */
export const useGetGenerationModelDetailByVersionId = modifyQueryHook(
  createQueryHook(GetGenerationModelDetailByVersionIdDocument),
  {
    transform: (id: string) => ({ id }),
    reduce: r => r?.generationModelVersion || undefined,
  },
)

// ==================================================
// User related
// ==================================================

/**
 * Impacts performance
 * Should only be used in User's own profile page
 */
const useListGenerationModelsWithTrainingTask = modifyQueryHook(
  createQueryHook(ListGenerationModelsWithTrainingTaskDocument),
  {
    reduce: r => r?.generationModels,
  },
)
export const useInfiniteListGenerationModelsWithTrainingTask =
  infiniteQueryHook(useListGenerationModelsWithTrainingTask)
export const usePagedListGenerationModelsWithTrainingTask = pagedQueryHook(
  useListGenerationModelsWithTrainingTask,
)

export const useListUserLikedGenerationModels = modifyQueryHook(
  createQueryHook(ListUserLikedGenerationModelDocument),
  {
    reduce: r => r?.user?.likedGenerationModelList,
  },
)

const _useListMyBookmarkedGenerationModels = modifyQueryHook(
  createQueryHook(ListMyBookmarkedGenerationModelsDocument, {
    context: { refetchOnFocus: false },
  }),
  {
    reduce: r => r?.me?.bookmarkedGenerationModels,
  },
)
const useListMyBookmarkedGenerationModels = (
  vars: Parameters<typeof _useListMyBookmarkedGenerationModels>[0],
  options?: Parameters<typeof _useListMyBookmarkedGenerationModels>[1],
) => {
  const input = useCheckTokenForArgs(vars)
  const res = _useListMyBookmarkedGenerationModels(input ?? skipToken, options)
  return res
}

export const useInfiniteListMyBookmarkedGenerationModels = infiniteQueryHook(
  useListMyBookmarkedGenerationModels,
)
export const usePagedListMyBookmarkedGenerationModels = pagedQueryHook(
  useListMyBookmarkedGenerationModels,
)

export const [useMarkGenerationModel, executeMarkGenerationModel] =
  createMutationHelper(MarkGenerationModelDocument, {
    update: (cache, result, args) => {
      const target = args.variables?.target
      const type = args.variables?.type
      const id = args.variables?.modelId
      if (!id) return

      cache.updateFragment(
        {
          fragment: GenerationModelDetailFragmentDoc,
          fragmentName: 'GenerationModelDetail',
          id: cache.identify({ __typename: 'GenerationModel' as const, id }),
        },
        data => {
          if (data) {
            const nextValue = !!target
            return match(type)
              .with(GenerationModelMarkType.Like, () => ({
                ...data,
                liked: nextValue,
              }))
              .with(GenerationModelMarkType.Bookmark, () => ({
                ...data,
                bookmarked: nextValue,
              }))
              .otherwise(() => undefined)
          }
          return undefined
        },
      )
    },
  })

// ==================================================
// Training Task related
// ==================================================

export const useGetTrainingTaskById = modifyQueryHook(
  createQueryHook(GetTrainingTaskByIdDocument),
  {
    transform: (id: string) => ({ id }),
  },
)

export const fetchGetTrainingTaskById = createFetchQueryHelper(
  GetTrainingTaskByIdDocument,
)

export const [, executeArchiveGenerateModelTrainingTask] = createMutationHelper(
  ArchiveTrainingTaskDocument,
)

export const [
  useReviewMemberOnlyModelEarlyAccess,
  executeReviewMemberOnlyModelEarlyAccess,
] = createMutationHelper(ReviewMemberOnlyModelEarlyAccessDocument)
